import type { NextPage, GetServerSideProps } from "next";

import { CacheByKeyService } from "@/backend/service/cache";
import { GrowthBookService } from "@/src/service/growthbook";
import { ProfileService } from "@/backend/service/profile";

import { ErrorBoundary } from "@/src/component/partial/ErrorBoundary";
import { SEO } from "@/src/component/partial/SEO";
import { DynamicPage } from "@/src/component/view/DynamicPage";
import { ProviderHooks } from "@/src/hook";
import CMSLogic from "@/src/logic/CMSLogic";
import ServerSidePageLogic from "@/src/logic/ServerSidePageLogic";
import { StrapiSingleType, StrapiCollectionType, BrokerProfileType, GrowthBookType } from "@/src/type";
import { NextButtonPosition } from "@/src/type/strapi/collection";
import { StringHelper, URLHelper, UserAgentHelper, UUIDHelper } from "@/src/util";

import theme from "@/styles/scss/theme.module.scss";

export const getServerSideProps: GetServerSideProps = async (context) => {
  const persistentId = context.req.cookies["PERSISTENT_ID"] ?? UUIDHelper.createUUID(true);
  context.res.setHeader("Set-Cookie", `PERSISTENT_ID=${persistentId}; Path=/; Max-Age=315360000; SameSite=Lax;`);
  context.res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");
  context.res.setHeader("Permissions-Policy", "xr-spatial-tracking=(self)");

  const { hostname, byPassCache, isPreview, returnHeaders, trackingOff, locale, isToolkit } = ServerSidePageLogic.getAllParams(context);
  const paths = context.params?.paths;

  if (Array.isArray(paths)) {
    const slug = paths.join("/");

    //transform data for the page
    const cacheResponse = await CacheByKeyService.cacheAllRequests(slug, hostname, locale, byPassCache);
    if (!cacheResponse?.page || !cacheResponse?.webConfig) {
      return {
        redirect: {
          permanent: false,
          destination: "/missing",
        },
      };
    }

    //get feature flags and experiments
    const growthBook = await GrowthBookService.getFeaturesAndExperiments({
      isToolkit,
      persistentId,
      url: context.req.url,
      path: (context.req.url ?? "").split("?")[0],
      host: hostname,
      slug,
      query: URLHelper.serializeParseUrlQuery(context.query),
      locale,
      contentVariants: cacheResponse.contentVariants ?? [],
    });

    //transform the data for the page
    const FF_enableTemplateExpressions = !!growthBook?.features?.["enable-template-expressions"]?.defaultValue;
    const galleries = cacheResponse.galleries;
    const webConfig = cacheResponse.webConfig;
    const page = cacheResponse.page;
    const domainData = ServerSidePageLogic.getDomainData(hostname, page);
    const tracking = { ...domainData?.tracking, ...page?.property?.data?.attributes?.tracking };
    const searchParams = context.query ?? {};
    const profileSlug: string | null = StringHelper.checkIsStringOrArrayAndIsNull(context.query?.["profile"], "");
    const profileResponse = profileSlug ? await ProfileService.getBySlug(profileSlug) : null;
    const currentSection = ServerSidePageLogic.getCurrentSection(searchParams, page);
    const headerInfo = ServerSidePageLogic.getHeaderInfoForCurrentSection(page, currentSection) ?? {
      header: null,
      backgroundColor: null,
    };

    if (page?.layout === "alternate" && !page?.headerObserver) {
      headerInfo["backgroundColor"] = theme.secondaryMain;
      headerInfo["header"] = ProviderHooks.STATIC_PAGE_HEADER;
    }

    if (page) {
      return {
        props: {
          page: ServerSidePageLogic.processPage(page, {}, FF_enableTemplateExpressions),
          webConfig,
          marketingSources: cacheResponse.marketing ?? null,
          locale: isPreview ? "en" : locale,
          layout: page.layout,
          query: URLHelper.serializeParseUrlQuery(context.query),
          searchParams,
          ...headerInfo,
          currentSection,
          profile: profileResponse?.[0] ?? null,
          refreshId: page.slug,
          noAuthRequired: true,
          trackingEnabled: !trackingOff,
          mobileHint: UserAgentHelper.isMobile(context.req?.headers?.["user-agent"]),
          deviceWidthHint: UserAgentHelper.deviceWidthHint(context.req?.headers?.["user-agent"]),
          appleOrMobileHint: UserAgentHelper.appleOrMobile(context.req?.headers?.["user-agent"]),
          domain: hostname,
          headers: returnHeaders ? (context.req?.headers ?? {}) : {},
          nav: cacheResponse.nav ?? null,
          tracking: tracking ?? null,
          domainData: domainData ?? null,
          persistentId,
          ...ServerSidePageLogic.getCurrentNextIcon(page, currentSection),
          isToolkit,
          galleries: galleries ?? null,
          growthBook: growthBook ?? {},
        },
      };
    }
  }

  return {
    redirect: {
      permanent: false,
      destination: "/missing",
    },
  };
};

interface DynamicPageProps {
  page: StrapiCollectionType.DynamicPage;
  pathOverride?: string;
  webConfig: StrapiSingleType.WebConfig;
  locale: string;
  searchParams?: Record<string, string | string[] | undefined>;
  profile?: BrokerProfileType.BrokerProfile | null;
  refreshId: string;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  appleOrMobileHint?: boolean;
  domain: string;
  domainData: StrapiCollectionType.Domain | null;
  headers: any;
  nextButtonText?: string;
  nextIconColor: "light" | "dark";
  nextButtonPosition?: NextButtonPosition;
  currentSection: number;
  propertySlug?: string;
  persistentId: string;
  growthBook: GrowthBookType.GrowthBookAndContentVariants;
}

const DynamicPageSSR: NextPage<DynamicPageProps> = ({
  page,
  pathOverride,
  webConfig,
  searchParams,
  profile,
  refreshId,
  mobileHint,
  deviceWidthHint,
  appleOrMobileHint,
  domain,
  headers,
  domainData,
  nextButtonText,
  nextIconColor,
  nextButtonPosition,
  currentSection,
  propertySlug,
  growthBook,
}) => {
  if (!page?.slug) {
    return (
      <ErrorBoundary forceFallback={true}>
        <></>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SEO
        title={page.seo?.title}
        byName={domainData?.siteName}
        pageDescription={page.seo?.description}
        description={domainData?.siteDescription ?? webConfig.description}
        domainImageURL={domainData?.siteDefaultImage?.data?.attributes?.url}
        pageImageURL={page.seo?.image?.data?.attributes?.url}
        favIcon={domainData?.favIcon?.data?.attributes?.url}
        webConfig={webConfig}
        path={page?.slug}
        keywords={CMSLogic.tagsToStringArray(page.seo?.keywords)}
        domain={domain}
        schemaOverride={page.seo?.schemaOverride}
        property={propertySlug ? page.property?.data?.attributes : undefined}
        canonical={page.seo?.canonical}
        pathOverride={pathOverride}
      />
      <DynamicPage
        page={page}
        searchParams={searchParams}
        profile={profile}
        refreshId={refreshId}
        mobileHint={mobileHint}
        deviceWidthHint={deviceWidthHint}
        appleOrMobileHint={appleOrMobileHint}
        domain={domain}
        headers={headers}
        defaultNextButtonText={nextButtonText}
        defaultNextIconColor={nextIconColor}
        defaultNextButtonPosition={nextButtonPosition}
        defaultActiveId={currentSection}
        webConfig={webConfig}
        growthBook={growthBook}
      />
    </ErrorBoundary>
  );
};

export default DynamicPageSSR;
